import { db } from "@/api/firebase";
import { Game } from "@/models/Game";
import { GameHistory } from "@/models/GameHistory";
import { GameType, GAMETYPE_ID, Season } from "@/models/GameType";
import { ManhuntMapPoi } from "@/models/MapPois/ManHunt/ManhuntMapPoi";
import { QuestMapPoi } from "@/models/MapPois/Quest/QuestMapPoi";
import QuestGameErrors from "@/utils/ErrorQuest/QuestGameErrors";
import QuestGameWarnings from "@/utils/ErrorQuest/QuestGameWarnings";
import ErrorsGame from "@/utils/ErrorsGame";
import HttpsHelper from "@/utils/HttpsHelper";
import WarningsGame from "@/utils/WarningsGame";
import { forIn, toArray } from "lodash";
import Vue from "vue";

import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

@Module({ namespaced: true })
export default class GameStore extends VuexModule {
  games: Game[] = [];
  gameType: GameType[] = [];
  historyGames: GameHistory[] = [];
  gameTypeSelected = GAMETYPE_ID.MANHUNT.toString();
  isEditing = false;
  loading = false;
  isGameEditing = false;
  isGameLoading = false;
  seasonData = {} as Season;
  //clonedRow = {} as string;

  @Mutation
  isLoading(loading: boolean): void {
    this.loading = loading;
  }
  @Mutation
  game(games: Game[]): void {
    this.games = games;
  }
  @Mutation
  historyGame(results: GameHistory[]): void {
    this.historyGames = results;
  }

  @Mutation
  gameTypeSelect(value: string): void {
    this.gameTypeSelected = value;
  }

  @Mutation
  gameTypes(results: GameType[]): void {
    this.gameType = results;
  }
  @Mutation
  season(results: Season): void {
    this.seasonData = results;
  }

  /*@Mutation
  cloneRow(clonedRow: string): void {
    this.clonedRow = clonedRow;
  }*/

  @Mutation
  currentlyEditing(value: boolean): void {
    this.isEditing = value;
  }
  @Mutation
  currentlyGameEditing(value: boolean): void {
    this.isGameEditing = value;
  }

  @Action({})
  public async getAllGames(): Promise<void> {
    await db.ref("games").off();
    const dbRef = await db.ref("games").orderByChild("start_dt");
    dbRef.on("child_added", (data) => {
      const games: Game = data.val();
      games.key = data.key;
      this.games.push(games);
    });
    dbRef.on("child_changed", (data) => {
      const results = this.games.findIndex((x: Game) => x.key === data.key);
      const games: Game = data.val();
      games.key = data.key;
      Vue.set(this.games, results, games);
    });
    dbRef.on("child_removed", (data) => {
      const results = this.games.findIndex((x: Game) => x.key === data.key);
      Vue.delete(this.games, results);
    });
  }
  @Action({ commit: "historyGame", rawError: true })
  public async getHistoryGames(): Promise<GameHistory[]> {
    this.context.commit("isLoading", true);
    const results: GameHistory[] = [];
    const historyKeys = await HttpsHelper.getHistoryGameShallow();
    const keys = Object.keys(historyKeys);
    await Promise.all(
      keys.map(async (key) => {
        try {
          const singleResult = await db
            .ref("leaderboards/" + key + "/meta/")
            .once("value");
          let game = {} as GameHistory;
          game = singleResult.val();
          game.key = key;
          results.push(game);
        } catch (error) {
          console.log(
            "The following leaderboard has an error: " +
              key +
              " error message: " +
              error
          );
        }
      })
    );
    this.context.commit("isLoading", false);
    return results;
  }
  @Action({ rawError: true })
  public async getGameTypes(selected: string): Promise<void> {
    await db
      .ref("gametypes")
      .child(selected)
      .child("seasons")
      .on("value", (results) => {
        this.context.commit("gameTypes", toArray(results.val()));
      });
  }

  @Action({ rawError: true })
  public async getSeason(data: any): Promise<void> {
    await db
      .ref("gametypes")
      .child(data.gameType)
      .child("seasons")
      .child(data.seasonUid)
      .on("value", (results) => {
        this.context.commit("season", results.val());
      });
  }

  @Action
  public async insertNewSeason(data: any): Promise<string> {
    try {
      const weaponKey = db
        .ref(
          "gametypes/" +
            GAMETYPE_ID.MANHUNT +
            "/seasons/" +
            data.slug +
            "/weapons"
        )
        .push().key as string;
      const weaponData = {
        name: { en: "default" },
        desc: { en: "" },
        img: "",
        guilt: false,
        uid: weaponKey,
      };
      db.ref("gametypes/" + GAMETYPE_ID.MANHUNT + "/seasons/" + data.slug).set({
        //In future we should make this a class and it will already be initalised
        slug: data.slug,
        teamSizeOverride: 6,
        notes: { en: data.notes },
        weapons: {
          [weaponKey]: weaponData,
        },
        outro: { outro_img: "", outro_t: { en: "" } },
        name: { en: data.seasonName },
        inv_items: {
          informant: {
            desc: { en: "Auto generated Inventory Item. DO NOT DELETE" },
            img: "",
            name: { en: "informant" },
            quant: 5,
            shown: true,
            slug: "informant",
          },
          wit_q: {
            desc: { en: "Auto generated Inventory Item. DO NOT DELETE" },
            img: "",
            name: { en: "wit_q" },
            quant: 30,
            shown: true,
            slug: "wit_q",
          },
        },
        intro: {
          intro_img: "",
          intro_t: { en: "" },
        },
        is_edit: false,
        imgs: [""],
        fx: {
          clock_adjust: {
            desc: "Auto generated FX. DO NOT DELETE",
            slug: "clock_adjust",
          },
          informant: {
            desc:
              "Auto generated FX by inventory item (informant) . DO NOT DELETE",
            slug: "informant",
          },
          wit_q: {
            desc: "Auto generated FX by inventory item (wit_q) . DO NOT DELETE",
            slug: "wit_q",
          },
        },
      });
      return "success";
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }

  @Action
  public async insertNewSeasonQuest(data: any): Promise<string> {
    try {
      db.ref("gametypes/" + GAMETYPE_ID.QUEST + "/seasons/" + data.slug).set({
        //In future we should make this a class and it will already be initalised
        slug: data.slug,
        teamSizeOverride: 6,
        outro: { outro_img: "", outro_t: { en: "" } },
        name: { en: data.seasonName },
        notes: { en: data.notes },
        inv_items: {
          gold: {
            desc: { en: "Gold Coins" },
            img: "",
            name: { en: "Gold" },
            quant: 10,
            shown: true,
            slug: "gold",
            type: "default",
          },
        },
        intro: {
          intro_img: "",
          intro_t: { en: "" },
        },
        is_edit: false,
        imgs: [""],
        fx: {
          clock_adjust: {
            desc: "Auto generated FX. DO NOT DELETE",
            slug: "clock_adjust",
          },
          gold: {
            desc: "Auto generated FX by inventory item (gold) . DO NOT DELETE",
            slug: "gold",
          },
        },
      });

      return "success";
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }

  @Action({ rawError: true })
  public async insertNewGame(data: any): Promise<string> {
    try {
      const games: Game = {
        admin_name: data.admin_name,
        city: data.city_uid,
        end_dt: data.end_dt,
        gt_uid: data.game_type,
        live: false,
        poi_count: 0,
        is_edit: false,
        publish_ready: true,
        publish_status: "none",
        req_lang: ["en"],
        seas_uid: data.seas_uid,
        solution: { suspect: {}, weapon: {} },
        clone_parent: "",
        start_dt: data.start_dt,
        team_count: 0,
        template: false,
        univ_voucher: "",
        v_count: 0,
        v_used: 0,
        notes: "",
        evergreen: data.evergreen,
        corp: { logoUrl: data.corpImage },
      };
      const key = await db.ref("/games/").push().key;
      if (key !== null) {
        await db.ref("/games/" + key).set(games);
        await db.ref("map_poi/" + key + "/START_ZONE").set({
          active: true,
          is_edit: false,
          loc: {
            geo_rad_m: 500,
            loc_uid: "",
            override_ll: {
              latitude: data.lat,
              longitude: data.lng,
            },
          },
          title: {
            en: "START_ZONE",
          },
          visible: false,
        });
      }
      return key || "";
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }

  @Action({ rawError: true })
  public async updateSeasonType(seasonData: Season): Promise<string> {
    try {
      await db
        .ref(
          "gametypes/" + this.gameTypeSelected + "/seasons/" + seasonData.slug
        )
        .update(seasonData);
      return "success";
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }

  @Action({ rawError: true })
  public async addEditLock(data: any): Promise<void> {
    try {
      this.context.commit("currentlyEditing", data.value);
      await db
        .ref("gametypes/" + this.gameTypeSelected + "/seasons/" + data.slug)
        .update({ is_edit: data.value });
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async addGameEditLock(data: any): Promise<void> {
    try {
      this.context.commit("currentlyGameEditing", data.value);
      await db.ref("games/" + data.slug).update({ is_edit: data.value });
      /*if (!data.value) {
        this.context.commit("cloneRow", data.slug);
      }*/
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async getKey(slug: string): Promise<string> {
    try {
      const myRef = await db
        .ref(
          "gametypes/" +
            this.gameTypeSelected +
            "/seasons/" +
            slug +
            "/weapons/"
        )
        .push();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return myRef.key!;
    } catch (error) {
      console.log(error);
      return "failed";
    }
  }
  //Need to grab game data - Copy that apart from city
  //If city is same grab poi and add to the game
  //If city is not same then grab poi and refresh location to 0,0
  @Action({ rawError: true })
  public async cloneGame(data: any): Promise<string> {
    try {
      const key = (await db.ref("/games/").push().key) || "";
      if (data.clonedGame.template) {
        data.clonedGame.clone_parent = data.clonedGame.key;
      }
      data.clonedGame.v_count = 0;
      data.clonedGame.v_used = 0;
      data.clonedGame.template = false;
      data.clonedGame.team_count = 0;
      data.clonedGame.univ_voucher = null;
      delete data.clonedGame.key;
      data.clonedGame.teams = null;
      data.clonedGame.is_edit = false;
      data.clonedGame.publish_status = "none";
      data.clonedGame.evergreen.isActive = false;
      data.clonedGame.evergreen.isClone = false;
      data.clonedGame.evergreen.cloneIDs = null;
      await db.ref("/games/" + key).set(data.clonedGame);
      if (data.clonedGame.gt_uid === "manhunt") {
        forIn(data.mapPoi, (x: ManhuntMapPoi) => {
          if (data.clonedGame.city !== data.clonedCity) {
            if (x.title.en === "START_ZONE") {
              x.loc.override_ll.latitude = data.cityData.centre_point.latitude;
              x.loc.override_ll.longitude =
                data.cityData.centre_point.longitude;
              x.loc.loc_uid = "";
            } else {
              x.loc.override_ll.latitude = 0.0;
              x.loc.override_ll.longitude = 0.0;
              x.loc.loc_uid = "";
            }
          }
          new WarningsGame(
            data.clonedGame,
            key,
            toArray(data.mapPoi)
          ).checkWarningsErrors();
          new ErrorsGame(
            key,
            toArray(data.mapPoi),
            data.cityData
          ).checkGameErrors();
        });
        await db.ref("map_poi/" + key).update(data.mapPoi);
      } else {
        await db.ref("quests/" + key).update(data.quest);
        forIn(data.mapPoi, (x: QuestMapPoi) => {
          if (data.clonedGame.city !== data.clonedCity) {
            if (x.title.en === "START_ZONE") {
              x.loc.override_ll.latitude = data.cityData.centre_point.latitude;
              x.loc.override_ll.longitude =
                data.cityData.centre_point.longitude;
              x.loc.loc_uid = "";
            } else {
              x.loc.override_ll.latitude = 0.0;
              x.loc.override_ll.longitude = 0.0;
              x.loc.loc_uid = "";
            }
          }
        });

        new QuestGameErrors(
          key,
          toArray(data.mapPoi),
          toArray(data.quest),
          data.cityData,
          data.seasonData,
          this.games
        ).checkGameErrors();
        new QuestGameWarnings(
          key,
          toArray(data.mapPoi),
          toArray(data.quest),
          data.cityData,
          data.seasonData,
          this.games
        ).checkGameWarnings();
        if (key === undefined) {
          console.log(data.mapPoi);
        }
        await db.ref("map_poi/" + key).update(data.mapPoi);
      }
      return key;
    } catch (error) {
      console.log(error);
      return "";
    }
  }

  @Action({ rawError: true })
  public async insertMiniGameStyle(data: any): Promise<void> {
    try {
      await db
        .ref(
          "gametypes/" + data.type + "/minigameStyles/" + data.name + "/code/"
        )
        .set({
          [data.number]: data.name,
        });
    } catch (error) {
      console.log(error);
    }
  }

  @Action({ rawError: true })
  public async updateGame(data: any): Promise<string> {
    try {
      if (data.notes === undefined) {
        data.notes = "";
      }
      if (data.corp === undefined) {
        data.corp = { logoUrl: "" };
      }
      await db.ref("/games/" + data.key).update({
        admin_name: data.admin_name,
        end_dt: data.end_dt,
        start_dt: data.start_dt,
        notes: data.notes,
        evergreen: data.evergreen,
        overrides: data.overrides,
        corp: data.corp,
      });
      return "success";
    } catch (error) {
      console.log(error);
      return "Failed";
    }
  }
  @Action({ rawError: true })
  public async deleteGame(data: any): Promise<string> {
    try {
      await db.ref("/games/" + data.key).update({
        live: false,
        template: false,
        end_dt: data.end_dt,
        start_dt: data.start_dt,
      });
      return "success";
    } catch (error) {
      console.log(error);
      return "Failed";
    }
  }

  @Action({ rawError: true })
  public async makeTemplate(data: string): Promise<void> {
    try {
      await db.ref("/games/" + data).update({
        template: true,
        clone_parent: "",
      });
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async makePublish(data: string): Promise<void> {
    try {
      await db.ref("/games/" + data).update({
        live: true,
      });
    } catch (error) {
      console.log(error);
    }
  }
  @Action({ rawError: true })
  public async makeDraft(data: string): Promise<void> {
    try {
      await db.ref("/games/" + data).update({
        live: false,
      });
    } catch (error) {
      console.log(error);
    }
  }
}
